import type { InstalledDestination } from "./destinations";
import type { TokenSet } from "../config/oauth";

export type ProjectId = string;
export type ClipId = string;

/**
 * What a project holds. `video` is the original OKB Studio project (camera
 * takes stitched into a film); `voice` records mic-only takes joined into one
 * audio file (for transcription downstream); `text` is a written note with no
 * media at all. Every kind publishes into the same OKB input feeds.
 */
export type ProjectKind = "video" | "voice" | "text";

/** Tab order on the home screen. */
export const PROJECT_KINDS: readonly ProjectKind[] = ["video", "voice", "text"];

export function isProjectKind(value: unknown): value is ProjectKind {
  return value === "video" || value === "voice" || value === "text";
}

/**
 * The kind carried by a URL's query string (`?kind=voice`), defaulting to
 * video. Used for the home tab and for a `/project/new` that has no stored
 * record to read a kind from yet.
 */
export function projectKindFromSearch(search: string): ProjectKind {
  const requested = new URLSearchParams(search).get("kind");
  return isProjectKind(requested) ? requested : "video";
}

/**
 * Where a publish goes: a space and one of its input feeds.
 *
 * Defined here rather than in default-feeds.ts because a project now carries
 * one of its own (`Project.publishTarget`) and types.ts must not import from a
 * module that imports it back.
 */
export interface FeedTarget {
  spaceId: string;
  feedId: string;
}

export interface Project {
  id: ProjectId;
  name: string;
  createdAt: number;
  updatedAt: number;
  clipIds: ClipId[];
  /** Absent on every project recorded before voice/text existed — those are
   * all video projects, so `undefined` reads as 'video' (see projectKind).
   * Never backfilled: an untouched record is cheaper than a migration. */
  kind?: ProjectKind;
  /** True while the name is still the generated "Project N" — cleared on
   * rename, and never set for caller-chosen names, so a deliberate name
   * (even one shaped like "Project 2") is never mistaken for the default. */
  nameIsDefault?: boolean;
  /**
   * Where this project publishes, chosen on the start screen or at publish
   * time. Optional in every sense: absent means "fall back to the Settings
   * default, then the last-used target", which is what every project did
   * before. Never required to record.
   */
  publishTarget?: FeedTarget;
  /**
   * How burned subtitles look across this project, and whether they burn at all.
   *
   * Absent means off, which is the only safe default: burning is pixels, not a
   * track, so a subtitle nobody asked for cannot be taken back out of a
   * published film.
   */
  subtitleStyle?: SubtitleStyle;
  /**
   * How long a still holds when its element sets no duration of its own, in ms.
   *
   * A project-level default with a per-element override, because a slideshow's
   * pace is a property of the film while one photo needing longer is a property
   * of that photo. Absent falls through to DEFAULT_PICTURE_HOLD_MS.
   */
  pictureHoldMs?: number;
  /**
   * Written captions, as runs of scenes with words over them.
   *
   * On the project row rather than in a store of their own: a block is two ids,
   * some text and a number, and putting them here costs no migration. The
   * "an edit must not rewrite the project" rule that keeps elements in their own
   * store is about records that carry blobs and come in fifties; this is neither.
   */
  captions?: CaptionBlock[];
}

/** A project's kind, defaulting pre-kind records to video. */
export function projectKind(project: Pick<Project, "kind">): ProjectKind {
  return isProjectKind(project.kind) ? project.kind : "video";
}

/** A voice/text project's body copy, keyed by project. Video projects never
 * have one; voice projects may carry hand-written notes published alongside
 * the audio, and for text projects this IS the project. */
export interface ProjectTextRecord {
  projectId: ProjectId;
  body: string;
  updatedAt: number;
}

/**
 * What a stored media item actually is.
 *
 * A picture reuses `ClipRecord` rather than getting a store of its own, which
 * buys the whole existing machinery for free — thumbnails, undo, deletion,
 * storage accounting, backup round-tripping. The cost is one discriminator and a
 * `durationMs` that means nothing for a still.
 *
 * ABSENT reads as 'video'. Every clip recorded before pictures existed is one,
 * and backfilling would rewrite every project on every phone to say what the
 * default already says.
 */
export type MediaKind = "video" | "picture";

export interface ClipMeta {
  id: ClipId;
  projectId: ProjectId;
  mimeType: string;
  /** Absent reads as 'video' — see MediaKind. */
  mediaKind?: MediaKind;
  /**
   * Media length. ZERO for a picture, which has none: how long a still occupies
   * the film is a property of the ELEMENT holding it, not of the file.
   */
  durationMs: number;
  trimStartMs: number;
  trimEndMs: number;
  createdAt: number;
  width?: number;
  height?: number;
  thumbWidth?: number;
  thumbHeight?: number;
  /** Opt-in geolocation captured at recording time (absent on older clips
   * and whenever tagging is off or unavailable). */
  lat?: number;
  lng?: number;
  locationAccuracyM?: number;
  /** This clip's own (foreground) sound level (0–1). Absent = full. */
  clipVolume?: number;
  /** Background-music level (0–1) while this clip plays, scaling the
   * playing track's own volume (duck the music under speech). Absent =
   * full (the track's volume alone). */
  musicVolume?: number;
  /** Measured whole-clip audio peak (0–1) at the export's mixing rate —
   * the persisted normalization measurement (0 = silent or undecodable).
   * Absent = not yet measured; backfilled automatically on project load. */
  audioPeak?: number;
}

export interface ClipRecord extends ClipMeta {
  blob: Blob;
  /** Filmstrip poster frames (JPEG). Generated lazily for old clips. */
  thumbs?: Blob[];
  /** High-resolution poster frame for the home slot art. */
  poster?: Blob;
}

export interface DeletedClipSnapshot {
  clip: ClipRecord;
  index: number;
  deletedAt: number;
}

/**
 * Why an audio segment is in the film — it decides its default level and
 * whether clips duck it.
 *
 * `music` is the original background bed: quiet by default
 * (DEFAULT_TRACK_VOLUME) and ducked under speech by each clip's `musicVolume`.
 * `voice` is narration recorded in the app: full by default and NOT ducked,
 * because ducking narration under the clip whose speech it replaces is
 * backwards.
 */
export type AudioSegmentRole = "music" | "voice";

/** One entry in a project's audio lane. */
export interface ProjectAudioTrack {
  id: string;
  blob: Blob;
  mimeType: string;
  durationMs: number;
  /** Display name (the picked file's name, or the take's label). */
  name: string;
  /**
   * The words THIS take is read from.
   *
   * Per take, not per project. The project script (see `Project.notes`) is one
   * document shared with the Notes tab and the camera's prompter, which is
   * right for a film read to camera in one go and wrong for voiceovers: a film
   * with six takes over six parts of the timeline has six different things to
   * say, and one shared script meant every take showed the same words and
   * editing one take's line rewrote the prompter for all of them.
   */
  script?: string | undefined;
  addedAt: number;
  /**
   * Where this segment starts in OUTPUT time.
   *
   * **Absent means "legacy sequential"** and is not backfilled. Every project
   * recorded before the audio lane was positioned holds a playlist whose
   * position was implied by order — track 0 at 0, each next one where the
   * previous ended — and those records live on users' phones. `resolveAudioLane`
   * derives a position for them on read, so nothing has to be migrated and no
   * existing music bed moves.
   *
   * A segment with a position and one without must not be mixed in the same
   * project; `resolveAudioLane` places positioned segments first and lays the
   * rest out sequentially after, which is the only reading that cannot silently
   * reorder someone's existing bed.
   */
  startMs?: number;
  /** Absent reads as 'music' — every legacy track is a background bed. */
  role?: AudioSegmentRole;
  /**
   * The run of SCENES this track is pinned across, inclusive.
   *
   * Both absent means the track is not pinned: it keeps its own `startMs` and
   * behaves exactly as it always did, whatever the lane's mode. That is the
   * default for every track ever recorded, so turning pinning on moves nothing
   * until a span is actually chosen.
   *
   * A run rather than a list, because "from this card to that one" is what the
   * operator picks and two ids are two things to keep valid rather than N. See
   * `lib/audio-spans.ts` for how it resolves.
   */
  spanFromElementId?: ElementId;
  spanToElementId?: ElementId;
  /** Kept range of the track (defaults to the whole file). Only this
   * window plays; the next track starts where it ends. */
  trimStartMs?: number;
  trimEndMs?: number;
  /** Track volume (0–1): the music's level under the clips while this
   * track plays (clips can duck it further via their own musicVolume).
   * Absent = DEFAULT_TRACK_VOLUME. */
  volume?: number;
  /** Ease this track in/out where it starts/ends. Absent = inherit the
   * playlist's fade flags (kept for records made before per-track fades). */
  fadeIn?: boolean;
  fadeOut?: boolean;
}

/**
 * A project's background music: tracks play one after the other under the
 * clips until the film ends (the last one is cut off there — nothing
 * loops), each at its own volume, which clips can duck individually.
 */
export interface ProjectAudioRecord {
  projectId: ProjectId;
  tracks: ProjectAudioTrack[];
  /** Playlist-level fade defaults, inherited by tracks without their own
   * fadeIn/fadeOut flags (records made before per-track fades). */
  fadeIn: boolean;
  fadeOut: boolean;
}

/** Default track volume: music at 25% sits under speech without drowning
 * it (both sides are peak-normalized, so 100% would rival the clips). */
export const DEFAULT_TRACK_VOLUME = 0.25;

/** The fields that shape how one audio segment plays back. `durationMs`
 * is optional so export-side track shapes (which may not know it) fit. */
export interface AudioTrackPlaybackFields {
  durationMs?: number;
  trimStartMs?: number;
  trimEndMs?: number;
  volume?: number;
  fadeIn?: boolean;
  fadeOut?: boolean;
  /** Decides the DEFAULT level when `volume` is unset. See audioTrackLevel. */
  role?: AudioSegmentRole;
}

export interface AudioTrackPlayback {
  trimStartMs: number;
  /** Infinity when the track length is unknown (export shapes without
   * durationMs and no explicit trim end). */
  trimEndMs: number;
  keptMs: number;
  volume: number;
  fadeIn: boolean;
  fadeOut: boolean;
}

/** Resolve a track's playback settings against its playlist's defaults:
 * trim clamped into the media, volume defaulting to DEFAULT_TRACK_VOLUME,
 * fades falling back to the playlist flags. */
export function resolveAudioTrackPlayback(
  track: AudioTrackPlaybackFields,
  playlist: { fadeIn: boolean; fadeOut: boolean },
): AudioTrackPlayback {
  const durationMs = track.durationMs ?? Infinity;
  const trimEndMs = Math.max(
    0,
    Math.min(track.trimEndMs ?? durationMs, durationMs),
  );
  const trimStartMs = Math.max(0, Math.min(track.trimStartMs ?? 0, trimEndMs));
  return {
    trimStartMs,
    trimEndMs,
    keptMs: Math.max(0, trimEndMs - trimStartMs),
    volume: audioTrackLevel(track),
    fadeIn: track.fadeIn ?? playlist.fadeIn,
    fadeOut: track.fadeOut ?? playlist.fadeOut,
  };
}

/** The track's kept (trimmed) length. */
export function audioTrackKeptMs(
  track: Pick<ProjectAudioTrack, "durationMs" | "trimStartMs" | "trimEndMs">,
): number {
  const end = Math.max(
    0,
    Math.min(track.trimEndMs ?? track.durationMs, track.durationMs),
  );
  const start = Math.max(0, Math.min(track.trimStartMs ?? 0, end));
  return Math.max(0, end - start);
}

/**
 * The segment's own level (0–1).
 *
 * The default depends on the ROLE, and the difference is not cosmetic: a music
 * bed sits under speech at DEFAULT_TRACK_VOLUME, while narration recorded in
 * the app is the speech — dropping it to 25% would make it inaudible under the
 * clip it is meant to carry. An explicit `volume` always wins.
 */
export function audioTrackLevel(
  track: Pick<AudioTrackPlaybackFields, "volume" | "role">,
): number {
  if (track.volume === undefined || !Number.isFinite(track.volume)) {
    return track.role === "voice" ? 1 : DEFAULT_TRACK_VOLUME;
  }
  return Math.max(0, Math.min(1, track.volume));
}

/** Total audio length if every segment were laid end to end. */
export function projectAudioTotalDurationMs(
  audio: Pick<ProjectAudioRecord, "tracks">,
): number {
  return audio.tracks.reduce((sum, track) => sum + audioTrackKeptMs(track), 0);
}

/**
 * The fields lane resolution needs.
 *
 * Looser than `ProjectAudioTrack` because the export builds its own track
 * shapes that carry playback data and a blob but no id or name — and the lane
 * has to resolve identically for both, or a film would be laid out one way in
 * the editor and another way on export.
 */
export interface AudioLaneFields {
  durationMs?: number;
  trimStartMs?: number;
  trimEndMs?: number;
  startMs?: number;
  role?: AudioSegmentRole;
}

/** A segment placed on the output timeline. */
export interface PositionedAudioSegment<
  T extends AudioLaneFields = ProjectAudioTrack,
> {
  /** Index into `audio.tracks`, so callers can get back to the blob. */
  index: number;
  track: T;
  role: AudioSegmentRole;
  /** Output time this segment starts at. */
  startMs: number;
  /** Output time it ends — `startMs + kept length`. */
  endMs: number;
  keptMs: number;
}

/** A segment's role, defaulting legacy records to the background bed. */
export function audioSegmentRole(
  track: Pick<ProjectAudioTrack, "role">,
): AudioSegmentRole {
  return track.role === "voice" ? "voice" : "music";
}

/**
 * A segment's kept length, tolerating an unknown `durationMs`.
 *
 * `audioTrackKeptMs` requires a duration because a stored track always has one.
 * An export-side shape may not, so the trim end stands in — and a segment whose
 * length cannot be established at all is reported as 0, which drops it rather
 * than letting `NaN` poison every later position in the lane.
 */
function laneKeptMs(track: AudioLaneFields): number {
  const duration = Number.isFinite(track.durationMs)
    ? (track.durationMs as number)
    : undefined;
  const rawEnd = track.trimEndMs ?? duration;
  if (rawEnd === undefined || !Number.isFinite(rawEnd)) return 0;
  const end = Math.max(0, Math.min(rawEnd, duration ?? rawEnd));
  const start = Math.max(0, Math.min(track.trimStartMs ?? 0, end));
  return Math.max(0, end - start);
}

/**
 * The audio lane, positioned in output time and ordered by start.
 *
 * The one function that reconciles two eras of the same record. A positioned
 * segment states its `startMs`; a LEGACY one does not, because before the lane
 * was positioned a track's place was implied by its order — track 0 at 0, each
 * next one where the previous one's kept window ended. Those records are on
 * users' phones, so they are read rather than migrated.
 *
 * Positioned segments are placed exactly where they say. Legacy ones are then
 * laid out sequentially among themselves, starting after the last positioned
 * segment ends — not from 0. An all-legacy playlist (every existing project)
 * therefore comes out byte-identical to the old sequential walk, which is the
 * property that matters: nobody's existing music bed moves.
 *
 * Overlaps are legal and expected on the way out; callers must handle a
 * position covered by more than one segment.
 */
export function resolveAudioLane<T extends AudioLaneFields>(audio: {
  tracks: readonly T[];
}): PositionedAudioSegment<T>[] {
  const placed: PositionedAudioSegment<T>[] = [];
  const legacy: { index: number; track: T; keptMs: number }[] = [];

  audio.tracks.forEach((track, index) => {
    const keptMs = laneKeptMs(track);
    // A zero-length segment cannot be heard and would make the sequential
    // cursor stand still, so it is dropped rather than placed.
    if (keptMs <= 0) return;
    if (typeof track.startMs === "number" && Number.isFinite(track.startMs)) {
      const startMs = Math.max(0, track.startMs);
      placed.push({
        index,
        track,
        role: audioSegmentRole(track),
        startMs,
        endMs: startMs + keptMs,
        keptMs,
      });
    } else {
      legacy.push({ index, track, keptMs });
    }
  });

  // Legacy segments follow everything positioned. Starting them at 0 instead
  // would drop a newly placed voiceover on top of an existing bed the operator
  // never asked to move.
  let cursor = placed.reduce((end, seg) => Math.max(end, seg.endMs), 0);
  for (const { index, track, keptMs } of legacy) {
    placed.push({
      index,
      track,
      role: audioSegmentRole(track),
      startMs: cursor,
      endMs: cursor + keptMs,
      keptMs,
    });
    cursor += keptMs;
  }

  // Ties broken by index so the order is deterministic — two segments dropped
  // at the same instant must not swap between reads.
  return placed.sort((a, b) => a.startMs - b.startMs || a.index - b.index);
}

/** Every segment sounding at `positionMs`. Empty where the lane is silent. */
export function audioSegmentsAtMs<T extends AudioLaneFields>(
  segments: readonly PositionedAudioSegment<T>[],
  positionMs: number,
): PositionedAudioSegment<T>[] {
  return segments.filter(
    (seg) => positionMs >= seg.startMs && positionMs < seg.endMs,
  );
}

/** Output time the audio lane stops sounding. */
export function audioLaneEndMs(
  segments: readonly PositionedAudioSegment<AudioLaneFields>[],
): number {
  return segments.reduce((end, seg) => Math.max(end, seg.endMs), 0);
}

export function clampVolume(volume: number): number {
  if (!Number.isFinite(volume)) return 1;
  return Math.max(0, Math.min(1, volume));
}

/** The clip's own (foreground) sound level (0–1); default full. */
export function clipSoundVolume(clip: Pick<ClipMeta, "clipVolume">): number {
  return clampVolume(clip.clipVolume ?? 1);
}

/** The music's level (0–1) while this clip plays, scaling the playing
 * track's own volume; default full (no duck). */
export function clipMusicVolume(clip: Pick<ClipMeta, "musicVolume">): number {
  return clampVolume(clip.musicVolume ?? 1);
}

export interface AppMeta {
  key: "settings";
  maxProjects: number;
  lastOpenedProjectId: ProjectId | null;
  onboardingDismissed: boolean;
  /** Home-page "Watch the tour" card dismissed (first-timer teaser). */
  tourCardDismissed?: boolean;
  /** Legacy upstream fields (Plus purchase / watermark opt-in) — retained in
   * the schema so imported metadata round-trips; nothing gates on them. */
  watermarkRemoved?: boolean;
  purchaseSessionId?: string | null;
  keepWatermark?: boolean;
  /** Opt-in: tag new clips with device location. */
  locationTaggingEnabled?: boolean;
  /**
   * Destinations installed on this device, oldest first.
   *
   * Device-local and never synced: there is no account to sync them to. Clear
   * the site data and the codes have to be entered again — the same bargain as
   * the projects themselves, and worth saying out loud wherever one is removed.
   */
  installs?: InstalledDestination[];
  /**
   * Platform OAuth tokens, on this device only.
   *
   * The Worker brokers the exchange because it holds the client secret, then
   * keeps nothing — there is no account to file them under. Clearing the site
   * data disconnects every platform, and the UI says so at the point of
   * connecting rather than leaving it to be found out.
   */
  connections?: TokenSet[];
  /** The persisted last export (OPFS-backed), recoverable after the share
   * sheet is missed — and served instantly when nothing changed. */
  lastExport?: {
    projectId: ProjectId;
    opfsName: string;
    mimeType: string;
    fileExtension: "mp4" | "webm";
    createdAt: number;
    /** Fingerprint of the clips (ids + trims) and watermark state that
     * produced the file — any difference means a fresh export is needed. */
    signature: string;
    watermarked: boolean;
  } | null;
}

/**
 * Legacy total-project cap. Still the default written into `AppMeta` (and
 * round-tripped through imported metadata), but the live gate is per-kind —
 * see MAX_PROJECTS_PER_KIND.
 */
export const MAX_PROJECTS = 6;

/**
 * How many projects each tab held, back when there were tabs.
 *
 * Still read by `maxProjectsFor`, which a couple of callers use to talk about a
 * legacy project's kind. The live gate is `MAX_PROJECTS_TOTAL`.
 */
export const MAX_PROJECTS_PER_KIND: Record<ProjectKind, number> = {
  video: 12,
  voice: 24,
  text: 48,
};

/**
 * How many projects there can be, now that there is one list.
 *
 * A unified project can hold camera takes, so it is nearer the old video cap
 * than the text one — but not equal to it, or someone with twenty voice notes
 * would find the app full the day this shipped. The storage meter is the real
 * guard; this is the guard against a list nobody can scroll.
 */
export const MAX_PROJECTS_TOTAL = 24;

export function maxProjectsFor(kind: ProjectKind): number {
  return MAX_PROJECTS_PER_KIND[kind];
}

/** Tab label. */
export const PROJECT_KIND_LABELS: Record<ProjectKind, string> = {
  video: "Video",
  voice: "Voice",
  text: "Text",
};

/** What one recorded item is called in this kind ("3 clips" / "3 takes"). */
export const PROJECT_KIND_ITEM_NOUN: Record<ProjectKind, string> = {
  video: "clip",
  voice: "take",
  text: "note",
};

/** Route id for a project that exists only as a URL until the first clip is
 * recorded — backing out of an empty "new project" leaves nothing behind. */
export const NEW_PROJECT_ID: ProjectId = "new";

/** Words in a text project's body — the only size that means anything there. */
export function countWords(body: string): number {
  const trimmed = body.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

export function effectiveDurationMs(
  clip: Pick<ClipMeta, "durationMs" | "trimStartMs" | "trimEndMs">,
): number {
  const end = Math.min(clip.trimEndMs, clip.durationMs);
  const start = Math.max(0, Math.min(clip.trimStartMs, end));
  return Math.max(0, end - start);
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const tenths = Math.floor((ms % 1000) / 100);
  if (minutes > 0) {
    return `${minutes}:${seconds.toString().padStart(2, "0")}.${tenths}`;
  }
  return `${seconds}.${tenths}s`;
}

export function newId(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/* ── the project document (sections + elements) ──────────────────────────── */

export type SectionId = string;
export type ElementId = string;

/**
 * What an element is made of.
 *
 * Note this is NOT `ProjectKind`. A project used to BE one of video/voice/text,
 * which meant a recording session that wanted a photo and a note had to be three
 * projects. Now a project holds elements, and these are the kinds an element can
 * be. `ProjectKind` survives only to read records written before this existed.
 */
/**
 * What an element puts on screen.
 *
 * There is deliberately no `voice`. A voice take has no picture of its own, so a
 * voice element on the picture timeline is a stretch of black with sound over it
 * — and the thing that would fix that (drawing a card behind it) is a TEXT
 * element, which already exists. Voiceovers belong in the project's positioned
 * audio lane, where `AudioSegmentRole` already has a `voice` role that is not
 * ducked under speech. That lane is parallel by design, which is what lets one
 * voiceover run across several elements.
 */
export type ElementKind = "video" | "picture" | "text" | "collage";

export function isElementKind(value: unknown): value is ElementKind {
  return (
    value === "video" ||
    value === "picture" ||
    value === "text" ||
    value === "collage"
  );
}

/**
 * How many things one collage can hold.
 *
 * The layout set is what caps it: `grid-2x2` is the widest arrangement anyone
 * has drawn geometry for, and a fifth item would have to be laid out by a rule
 * nobody checked.
 */
export const COLLAGE_ITEM_MAX = 4;

/**
 * How several media items in one element are arranged in the frame.
 *
 * A deliberately closed set. Each one is a hand-checked geometry (see
 * `lib/collage-layout.ts`), because a layout the render and the preview disagree
 * about is a film that does not look like what was built. `single` is the
 * one-item case and is not a layout so much as the absence of one.
 */
export type CollageLayout =
  | "single"
  | "side-by-side"
  | "stacked"
  | "pip"
  | "pip-top"
  | "grid-2x2"
  | "grid-3-left"
  | "feature-top";

/**
 * Every arrangement, in the order a picker should offer them: by how many
 * items they hold, so the list reads 1, 2, 2, 2, 3, 3, 4 rather than by
 * whichever was added when.
 */
export const COLLAGE_LAYOUTS: readonly CollageLayout[] = [
  "single",
  "side-by-side",
  "stacked",
  "pip",
  "pip-top",
  "grid-3-left",
  "feature-top",
  "grid-2x2",
];

/** How many items each layout is drawn for. */
export const COLLAGE_CAPACITY: Record<CollageLayout, number> = {
  single: 1,
  "side-by-side": 2,
  stacked: 2,
  pip: 2,
  "pip-top": 2,
  "grid-2x2": 4,
  "grid-3-left": 3,
  "feature-top": 3,
};

/**
 * The layout that fits `count` items with no gaps, for a fresh element.
 *
 * Also the answer to "the operator added a third item, now what": a collage
 * whose count no longer matches its layout is re-laid-out rather than dropping
 * the item it has no cell for.
 */
export function defaultLayoutFor(count: number): CollageLayout {
  if (count <= 1) return "single";
  // Side-by-side rather than `pip` for a fresh pair: both items are equally
  // visible, which is what someone who has not chosen an arrangement means.
  // Picture-in-picture is a deliberate choice about which one is the subject.
  if (count === 2) return "side-by-side";
  if (count === 3) return "grid-3-left";
  return "grid-2x2";
}

/**
 * A named group of elements inside a project.
 *
 * Sections exist so a long shoot can be organised as it is recorded rather than
 * afterwards — "intro", "the demo", "outro" — and so elements can be moved
 * between them. A project always has at least one; the first is named at
 * creation and defaults to "Section 1".
 */
export interface ProjectSection {
  id: SectionId;
  projectId: ProjectId;
  name: string;
  /**
   * What this section is for, as markdown.
   *
   * The same guidance an element carries, one level up: a section is a beat of
   * the film, and what the beat is meant to do is worth saying once rather than
   * on each element inside it.
   */
  notesMd?: string;
  /** Order within the project. Ties break by id so a render is deterministic. */
  sort: number;
  createdAt: number;
}

/**
 * One thing in a project: a take, a photo or collage, a voice note, or a block
 * of text.
 *
 * `clipIds` carries the media — one item is singular, several is a collage. Text
 * elements have none. The list is ordered, and that order is the collage's
 * reading order, so swapping two photos in a grid is a reorder here rather than
 * a layout change.
 */
export interface ProjectElement {
  id: ElementId;
  projectId: ProjectId;
  sectionId: SectionId;
  kind: ElementKind;
  /** Order within the section. */
  sort: number;
  /** Ordered media items. Empty for `text` and for `collage`. */
  clipIds: ClipId[];
  /**
   * Arrangement when there is more than one thing to place — several clips in
   * one element, or a `collage`'s items. Absent reads as `single`.
   */
  layout?: CollageLayout;
  /**
   * A `collage`'s contents: OTHER elements of this project, in reading order.
   *
   * Elements rather than clips, and that is the whole point of the kind. A
   * multi-clip element could already put four photographs in one frame, but
   * every cell had to be a piece of media — so a shot beside a caption, or two
   * takes either side of a title, was not something the model could say. A
   * collage's cells are elements, so each one carries whatever that element
   * carries: a take with its trim, a still, a card with its own colours.
   *
   * The items usually also appear in the film in their own right, which is
   * rarely wanted — a shot used inside a collage is not normally also a shot —
   * so the editor offers `hiddenFromFilm` on each item as it is added. That flag
   * lives on the ITEM element, not here: "in the film" is a fact about that
   * element, and a collage is not the only thing that can want a picture kept
   * out of the cut.
   *
   * A collage may not contain a collage. Nothing in the model needs the
   * recursion, and the arithmetic that decides how long one lasts would have to
   * guard against a cycle rather than simply reading its items.
   */
  itemElementIds?: ElementId[];
  /**
   * A text element's CARD COPY — the words rendered into the film.
   *
   * Only a `text` element uses it, and a text element without it renders nothing
   * and is skipped. It used to double as the description for every kind, which
   * conflated two different things: what the card SAYS and what the element is
   * FOR. Those are now `contentMd` and `notesMd`.
   */
  contentMd?: string;
  /**
   * What this element is for, as markdown.
   *
   * Guidance rather than payload: never drawn into the film, and carried on
   * every kind. It is what tells a person — or a model reading the published
   * project — why this shot is here and how it is meant to be used, which is
   * exactly what a card's own copy cannot say without becoming part of the film.
   */
  notesMd?: string;
  /**
   * Kept in the project, kept out of the film.
   *
   * A picture taken to sit behind a card's words is a real part of the project
   * — it has to stay in the media library for the card to point at — and it is
   * not a shot. Deleting it to keep it out of the film would take the card's
   * background with it, so "in the project" and "in the film" had to stop being
   * the same question.
   *
   * Absent reads as visible, so nothing recorded before this appears or
   * disappears.
   */
  hiddenFromFilm?: boolean;
  /**
   * How long this element occupies the film, when its media does not say.
   *
   * A `video` element derives its length from its clips' trims and ignores this.
   * A `picture` or `text` element has no inherent length, so it holds for this
   * long — falling back to the project's `pictureHoldMs`. A `voice` element
   * derives from its audio but may be padded by this to hold its card longer.
   */
  durationMs?: number;
  /**
   * What a card is drawn on: a colour, or one of the project's pictures.
   *
   * Only a `text` element uses it — everything else has media filling the frame
   * already. A picture behind the words is why this is not just a colour field:
   * a title over the shot it introduces is the ordinary case, and it has to come
   * from the media the operator already has rather than from an upload.
   *
   * Absent means the default dark ground. Both may be set: the colour shows while
   * the picture loads, and shows through if it cannot be read at all.
   */
  cardBackground?: CardBackground;
  /**
   * Burned-in subtitle cues for this element, in ELEMENT-local time.
   *
   * Element-local rather than film-local, and stored rather than derived at
   * export time. Both choices are about surviving an edit: reorder two elements
   * and film-local cues are all wrong, while element-local ones are still right
   * and simply offset differently — and transcribing at export would mean a
   * network round trip per element every time the film is rendered.
   *
   * Absent means this element is not subtitled, which is different from the
   * project having subtitles off.
   */
  subtitles?: SubtitleCue[];
  /**
   * Words drawn OVER this element's picture, placed by hand.
   *
   * Not the same thing as a `text` element's `contentMd`, and deliberately a
   * different field: a card IS words on a ground, laid out to fill the frame,
   * while this is a label on top of a shot — a name, a location, a figure — that
   * has to sit somewhere specific because what is behind it decides where it can
   * go. That is also why the position is stored rather than chosen from a set of
   * slots: "clear of her face" is not a slot.
   *
   * Absent on a `text` element, which has no picture to label. Absent everywhere
   * else means no overlay, so nothing recorded before this grows one.
   */
  overlay?: TextOverlay;
  createdAt: number;
  updatedAt: number;
}

/**
 * How a plate sits behind overlay words.
 *
 * Three rather than the two "solid or transparent" suggests, because over
 * footage the useful answer is usually neither: a solid block hides the shot it
 * is labelling, and nothing at all leaves white text illegible the moment the
 * picture goes pale. `soft` is a translucent plate, which is what a lower third
 * actually is.
 */
export type OverlayPlate = "solid" | "soft" | "none";

/**
 * Words over an element's picture.
 *
 * Placement is in FRACTIONS of the frame, like a collage's cells and for the
 * same reason: the editor places it at preview size and the export composites
 * at output size, and a position computed twice from different pixel dimensions
 * lands in two different places. The fraction is the block's own top-left.
 */
export interface TextOverlay {
  /** What it says. Plain text — line breaks are honoured, markdown is not. */
  text: string;
  /** Top-left of the text block, as fractions of the frame. */
  x: number;
  y: number;
  /** Type size as a fraction of frame HEIGHT, so it scales with the film. */
  sizeRatio?: number;
  /** The ink. */
  color: string;
  /** The plate's colour, used by `solid` and `soft`. */
  background?: string;
  plate?: OverlayPlate;
}

/** A fresh overlay: readable over anything, near the bottom, out of the way. */
export const DEFAULT_TEXT_OVERLAY: Omit<TextOverlay, "text"> = {
  x: 0.08,
  y: 0.78,
  sizeRatio: 0.055,
  color: "#ffffff",
  background: "#000000",
  plate: "soft",
};

/** Whether this kind of element has a picture worth labelling. */
export function canHaveOverlay(kind: ElementKind): boolean {
  // A text element is already words on a ground; a second set of words placed
  // over them is a card fighting itself.
  return kind !== "text";
}

/**
 * The colour pairs a card can take.
 *
 * PAIRS, not a background and a foreground chosen separately. Independent
 * choices are how a card ends up white on white — which is exactly what
 * happened, because the ground was selectable and the ink was not. Every entry
 * here is a combination someone can read.
 */
export const CARD_THEMES: {
  id: string;
  label: string;
  background: string;
  color: string;
}[] = [
  { id: "dark", label: "Dark", background: "#0b0b0b", color: "#f5f5f5" },
  { id: "light", label: "Light", background: "#f5f5f5", color: "#141414" },
  { id: "deep", label: "Deep blue", background: "#12233d", color: "#f2f6ff" },
  { id: "warm", label: "Warm", background: "#f6efe4", color: "#2a1d0e" },
  { id: "ink", label: "Ink", background: "#1b2a24", color: "#e8f3ed" },
];

/** The pair for a stored background colour, or the default dark one. */
export function cardThemeFor(background: string | undefined): {
  background: string;
  color: string;
} {
  const found = CARD_THEMES.find((theme) => theme.background === background);
  return found ?? CARD_THEMES[0]!;
}

/** What a text card is drawn on. */
export interface CardBackground {
  /** A solid ground, as `#rrggbb`. */
  color?: string;
  /**
   * A picture from the project, drawn cover-cropped behind the words.
   *
   * A clip id rather than a blob: the picture is already in the project, and
   * copying its bytes into the element would be a second copy to keep in step.
   */
  clipId?: ClipId;
  /**
   * How much to darken the picture, 0–1, so the words stay legible over it.
   *
   * Not optional in spirit: text over an un-dimmed photograph is unreadable
   * about half the time, and which half depends on the photograph. Defaults to a
   * real scrim rather than to none.
   */
  scrim?: number;
}

/** The scrim a card's background picture gets when it states none. */
export const DEFAULT_CARD_SCRIM = 0.45;

/** Default seconds a still holds when neither element nor project says. */
export const DEFAULT_PICTURE_HOLD_MS = 3000;

/**
 * How burned subtitles look, for the whole project.
 *
 * Project-level rather than per-element on purpose: subtitles that change font
 * halfway through a film look like a mistake, and the per-element decision that
 * IS worth having is whether an element is subtitled at all.
 */
export interface SubtitleStyle {
  /** Off entirely — the default, since burned text cannot be removed later. */
  enabled: boolean;
  /** Fraction of the frame height for the type. */
  sizeRatio?: number;
  /** Fill, and the outline that keeps it legible over any footage. */
  color?: string;
  outline?: string;
  /** Vertical placement as a fraction of frame height from the top. */
  positionRatio?: number;
  /** Draw a translucent plate behind the text. */
  plate?: boolean;
  /** UPPERCASE, which some brands want and most do not. */
  uppercase?: boolean;
}

export const DEFAULT_SUBTITLE_STYLE: SubtitleStyle = {
  enabled: false,
  sizeRatio: 0.045,
  color: "#ffffff",
  outline: "#000000",
  positionRatio: 0.82,
  plate: true,
  uppercase: false,
};

/**
 * A run of scenes with words to put over them, one caption per LINE.
 *
 * Captions used to be TRANSCRIBED — a machine listened to an element and wrote
 * timed cues onto it — which is the right shape for subtitling speech and the
 * wrong one for writing captions. Writing them meant editing a list of cues
 * with millisecond boundaries, per element, and a caption that should span a cut
 * had to be written twice.
 *
 * A block inverts it: pick the first and last scene, type the words, and each
 * line holds for `lineMs` before the next. The scenes are the timing, so
 * retrimming a shot moves the captions with it — the same bargain a pinned
 * voiceover makes (see `lib/audio-spans.ts`), for the same reason.
 *
 * Cues are still what the film burns. `captionCuesByElement` turns a block into
 * element-local cues, splitting a line that crosses a cut, so the burn and the
 * manifest are untouched by any of this.
 */
export interface CaptionBlock {
  id: string;
  /** The run of scenes these captions play over, inclusive. */
  fromElementId: ElementId;
  toElementId: ElementId;
  /** The words. One caption per line; blank lines are gaps. */
  text: string;
  /** How long each line holds. Absent = DEFAULT_CAPTION_LINE_MS. */
  lineMs?: number;
}

/**
 * How long one caption line holds when a block states nothing.
 *
 * Two and a half seconds is about six words at a readable pace, which is what a
 * single caption line should be.
 */
export const DEFAULT_CAPTION_LINE_MS = 2500;

/** One timed subtitle cue, in element-local milliseconds. */
export interface SubtitleCue {
  startMs: number;
  endMs: number;
  text: string;
}

/**
 * A project's whole document: its sections, its elements, and the media each
 * element points at.
 *
 * Resolved rather than stored as one blob — the pieces live in their own stores
 * so a single element edit does not rewrite the project. See
 * `lib/project-document.ts`, which is also where a legacy video/voice/text
 * project is adapted into this shape on read.
 */
export interface ProjectDocument {
  sections: ProjectSection[];
  elements: ProjectElement[];
}

/** Elements of one section, in order. */
export function sectionElements(
  doc: ProjectDocument,
  sectionId: SectionId,
): ProjectElement[] {
  return doc.elements
    .filter((element) => element.sectionId === sectionId)
    .sort((a, b) => a.sort - b.sort || a.id.localeCompare(b.id));
}

/** Sections in order. */
export function orderedSections(doc: ProjectDocument): ProjectSection[] {
  return [...doc.sections].sort(
    (a, b) => a.sort - b.sort || a.id.localeCompare(b.id),
  );
}

/**
 * Every element in the order it plays: sections in order, elements within each.
 *
 * The render's reading order, and the only place that flattening should happen —
 * two callers each doing their own sort is how a preview and an export come to
 * disagree.
 */
export function documentTimeline(doc: ProjectDocument): ProjectElement[] {
  return orderedSections(doc).flatMap((section) =>
    sectionElements(doc, section.id),
  );
}
